import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import AppContext from 'context/Context';
import { getColor } from 'helpers/utils';
import { toast } from 'react-toastify';
import apiAxios from 'helpers/apiAxios';

// Upload pasted/inserted images to S3 (ActiveStorage) and embed the returned URL
// instead of inline base64 — keeps rich-text HTML (and the DB) small. On failure
// we fall back to base64 so the image is never lost.
const imagesUploadHandler = (blobInfo) =>
  new Promise((resolve) => {
    const form = new FormData();
    form.append('image', blobInfo.blob(), blobInfo.filename());
    apiAxios
      .post('/rich_text_images', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => resolve(res?.data?.data?.url))
      .catch(() => {
        toast.warn('Image upload failed — kept inline temporarily.', {
          toastId: 'img-upload-fail',
          autoClose: 4000
        });
        resolve(`data:${blobInfo.blob().type};base64,${blobInfo.base64()}`);
      });
  });

const IMAGE_MAX_FILE_KB = 500;

/**
 * Make an <img> responsive: it scales down to fit its container / the PDF page
 * but never forces a fixed pixel width — large uploads are allowed and the user
 * can freely resize with the drag handles. Mutates the element in-place.
 */
const constrainImage = (img) => {
  img.removeAttribute('width');
  img.removeAttribute('height');
  img.style.removeProperty('width');
  img.style.maxWidth = '100%';
  img.style.height   = 'auto';
};

/**
 * `resizable` puts TinyMCE's own drag handle at the bottom-right so the writer
 * can stretch the editor while composing. The handle lives in the status bar,
 * so the bar is turned on with it (element path and branding stay hidden);
 * `minHeight`/`maxHeight` bound how far it can be dragged.
 */
const TinymceEditor = ({
  value, initialValue, handleChange, height = '50vh', readonly = false,
  resizable = false, minHeight, maxHeight, initOverride = {}, rollback
}) => {
  const {
    config: { isDark, isRTL }
  } = useContext(AppContext);

  const editorRef = useRef(null);

  // Opt-in only. tinymce-react re-applies the previous `value` 200ms after a
  // keystroke when the prop has not caught up, which drops characters on forms
  // heavy enough to render slowly (a PIF mounts dozens of editors). Left at the
  // library default everywhere else — this component has 60+ call sites and
  // changing all of them at once is not worth the blast radius.
  const rollbackProp = rollback === undefined ? {} : { rollback };

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.dom.addStyle(
        `body{color: ${getColor('white')} !important;}`
      );
    }
  }, [isDark]);

  return (
    <Editor
      tinymceScriptSrc='/tinymce/tinymce.min.js'
      onInit={(_evt, editor) => {
        editorRef.current = editor;
      }}
      {...(initialValue !== undefined ? { initialValue } : { value })}
      {...rollbackProp}
      onEditorChange={handleChange}
      licenseKey='gpl'
      init={{
        readonly,
        height,
        menubar: false,
        // img rule keeps embedded images from overflowing the PDF page
        content_style: `
          body { color: ${getColor('black')}; font-family: Helvetica, Arial, sans-serif; font-size: 14px; }
          img  { max-width: 100%; height: auto; }
        `,

        // Strip layout-breaking inline styles from ALL content (pasted, typed, or set
        // programmatically) at the source, so the SAVED HTML never carries a tight
        // line-height, stray margins, or absolute positioning. When that HTML is later
        // rendered raw (dangerouslySetInnerHTML / wkhtmltopdf PDF) those styles made
        // text overlap its own lines or its table cell. Visible formatting — colour,
        // bold/italic, alignment, background — is preserved. Root-level counterpart to
        // the render-time normalisation on the CO description cells.
        //   • line-height / position: stripped everywhere (never wanted in note content).
        //   • margin: stripped only on text blocks (p/span/div) — NOT on lists, whose
        //     margins are legitimate indentation.
        invalid_styles: {
          '*':    'line-height position top right bottom left',
          'p':    'margin margin-top margin-right margin-bottom margin-left',
          'span': 'margin margin-top margin-right margin-bottom margin-left',
          'div':  'margin margin-top margin-right margin-bottom margin-left'
        },
        mobile: {
          theme: 'mobile',
          toolbar: ['undo', 'bold']
        },
        statusbar: resizable,
        resize: resizable,
        elementpath: false,
        branding: false,
        ...(minHeight ? { min_height: minHeight } : {}),
        ...(maxHeight ? { max_height: maxHeight } : {}),
        plugins: 'link image lists table directionality help',
        toolbar:
          'styleselect | bold italic strikethrough link bullist numlist image blockquote table | forecolor backcolor | undo redo | help | fontsize',
        directionality: isRTL ? 'rtl' : 'ltr',
        convert_urls: false,
        relative_urls: false,
        remove_script_host: false,
        document_base_url: process.env.REACT_APP_CLIENT_URL,
        theme_advanced_toolbar_align: 'center',
        images_reuse_filename: true,
        images_upload_max_size: IMAGE_MAX_FILE_KB * 1024,
        images_file_types: 'jpeg,jpg,png,gif,webp',

        // ── Upload images to S3 instead of inlining base64 ─────────────────
        automatic_uploads: true,
        paste_data_images: true, // route pasted images through the upload handler
        images_upload_handler: imagesUploadHandler,

        // ── Paste: auto-constrain images instead of blocking ──────────────
        // paste_postprocess fires inside TinyMCE's paste pipeline (synchronous),
        // before content is committed to the editor. This is the only reliable
        // hook for modifying pasted HTML in wkhtmltopdf-targeted content.
        paste_postprocess: (_editor, args) => {
          args.node.querySelectorAll('img').forEach(img => {
            constrainImage(img);
          });
        },

        // Keep the browser's default image resize handles enabled.
        object_resizing: true,

        // Stamp an INLINE `max-width:100%` on every image so the constraint travels
        // with the saved HTML. This is what keeps large user images from breaking
        // the PIF display/print (rendered via dangerouslySetInnerHTML, without the
        // editor's content_style). We only add max-width — never a fixed width — so
        // drag-resizing still works.
        setup: (editor) => {
          const makeResponsive = () => {
            editor.getBody()?.querySelectorAll('img').forEach((img) => {
              if (img.style.maxWidth !== '100%') img.style.maxWidth = '100%';
              if (!img.style.height) img.style.height = 'auto';
            });
          };
          editor.on('SetContent NodeChange', makeResponsive);
        },
        ...initOverride,
      }}
    />
  );
};

TinymceEditor.propTypes = {
  value: PropTypes.string,
  initialValue: PropTypes.string,
  handleChange: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readonly: PropTypes.bool,
  resizable: PropTypes.bool,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  initOverride: PropTypes.object,
  // false disables tinymce-react's 200ms content rollback; omit to keep it.
  rollback: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

export default TinymceEditor;
